import React from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle, CheckCircle, Clock,
  Phone, MessageCircle, ChevronRight, User, Pill,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "../../lib/utils";
import { Badge, Button } from "../ui";

type AlertLevel  = "level1_patient" | "level2_caregiver" | "level3_hospital" | "level4_emergency";
type AlertStatus = "pending" | "escalated" | "acknowledged" | "resolved";

interface Alert {
  id:          string;
  patientName: string;
  age:         number;
  title:       string;
  time:        Date;
  level:       AlertLevel;
  status:      AlertStatus;
  medication:  string;
  phone:       string;
}

interface AlertFeedProps {
  alerts?:         Alert[];
  onAcknowledge?:  (id: string) => void;
}

const levelConfig: Record<AlertLevel, { label: string; badge: "red" | "orange" | "blue" | "gray"; icon: React.ReactNode; bg: string }> = {
  level1_patient:   { label: "ผู้ป่วย",    badge: "blue",   icon: <User className="w-3.5 h-3.5" />,           bg: "bg-blue-50"   },
  level2_caregiver: { label: "ผู้ดูแล",    badge: "orange", icon: <AlertTriangle className="w-3.5 h-3.5" />,   bg: "bg-orange-50" },
  level3_hospital:  { label: "รพสต./รพ.",  badge: "red",    icon: <AlertTriangle className="w-3.5 h-3.5" />,   bg: "bg-red-50"    },
  level4_emergency: { label: "ฉุกเฉิน",   badge: "red",    icon: <AlertTriangle className="w-3.5 h-3.5" />,   bg: "bg-red-100"   },
};

export function AlertFeed({ alerts = [], onAcknowledge = () => {} }: AlertFeedProps) {
  return (
    <div className="divide-y divide-gray-50">
      {alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <CheckCircle className="w-12 h-12 mb-3 text-green-300" />
          <p className="font-medium text-gray-500">ไม่มีการแจ้งเตือนที่รอดำเนินการ</p>
          <p className="text-sm mt-1">ทุกอย่างเรียบร้อยดี 🎉</p>
        </div>
      ) : (
        alerts.map((alert, i) => {
          const cfg     = levelConfig[alert.level];
          const isPending = alert.status === "pending" || alert.status === "escalated";

          return (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className={cn(
                "flex items-start gap-4 px-6 py-4 transition-colors",
                isPending && "hover:bg-gray-50/80",
                alert.level === "level3_hospital" && isPending && "bg-red-50/40 hover:bg-red-50/70",
              )}
            >
              {/* Level Icon */}
              <div className={cn("p-2.5 rounded-xl mt-0.5 shrink-0", cfg.bg)}>
                <div className={cn(
                  "w-4 h-4",
                  alert.level === "level3_hospital" ? "text-red-500" :
                  alert.level === "level2_caregiver" ? "text-orange-500" : "text-blue-500"
                )}>
                  {cfg.icon}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{alert.patientName}</p>
                      <span className="text-xs text-gray-400">อายุ {alert.age} ปี</span>
                      <Badge
                        variant={alert.status === "acknowledged" ? "green" : cfg.badge}
                        dot
                      >
                        {alert.status === "acknowledged" ? "รับทราบแล้ว" : cfg.label}
                      </Badge>
                    </div>
                    <p className={cn(
                      "text-sm mt-0.5 font-medium",
                      isPending ? "text-gray-800" : "text-gray-500"
                    )}>
                      {alert.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      <Pill className="w-3 h-3 inline-block mr-1 -mt-0.5" />{alert.medication}
                    </p>
                  </div>

                  {/* Time */}
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(alert.time, { addSuffix: true, locale: th })}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                {isPending && (
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<CheckCircle className="w-3.5 h-3.5" />}
                      onClick={() => onAcknowledge(alert.id)}
                    >
                      รับทราบ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Phone className="w-3.5 h-3.5" />}
                    >
                      โทร
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<MessageCircle className="w-3.5 h-3.5 text-green-500" />}
                    >
                      LINE
                    </Button>
                  </div>
                )}
              </div>

              <ChevronRight className="w-4 h-4 text-gray-300 mt-1 shrink-0" />
            </motion.div>
          );
        })
      )}
    </div>
  );
}
